import React, { useCallback, useEffect, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { Map } from 'immutable';
import moment from "moment";
import { setSideNav } from "../../../redux/recruiter/actions";
import { 
  resetIsUpdateSuccessful,
  fetchApplicantsListRequest,
  fetchApplicantsAfterAYearCount,
  fetchTotalApplicantsRequest,
  resetApplicantsState,
} from "../../../redux/applicant/actions";
import { clearSpecificRequest } from "../../../redux/request/action";
import { 
  SEARCH_APPLICANT_REQUEST,
  FETCH_APPLICANTS_LIST_REQUEST,
} from "../../../redux/applicant/actionTypes";
import NewApplicantsPage from "./NewApplicantsPage";
import { arrayToObject } from "../../../utils/helpers/transform";

const NewApplicantsPageContainer = () => {
  const [readLastElement, setReadLastElement] = useState(undefined);
  const [unreadLastElement, setUnreadLastElement] = useState(undefined);
  const [readApplicantsData, setReadApplicantsData] = useState({});
  const [unreadApplicantsData, setUnreadApplicantsData] = useState({});
  const [readPageSize, setReadPageSize] = useState(10);
  const [unReadPageSize, setUnreadPageSize] = useState(10);
  const [currentReadPage, setCurrentReadPage] = useState(1);
  const [currentUnreadPage, setCurrentUnreadPage] = useState(1);
  const [activeTab, setActiveTab] = useState('0');

  const dispatch = useDispatch();

  const sideNav = useSelector(
    (state: RootStateOrAny) => state.recruiter.sidenav
  );

  const applicantsList = useSelector(
    (state: RootStateOrAny) => state.requestStore?.[FETCH_APPLICANTS_LIST_REQUEST],
  );

  const afterAYearApplicants = useSelector(
    (state: RootStateOrAny) => state.applicant.invalidApplicantsTotal,
  );

  const totalApplicants = useSelector(
    (state: RootStateOrAny) => state.applicant.totalApplicants,
  );

  const readTotal = (totalApplicants?.newApplicant?.read || 0) - afterAYearApplicants.read;
  const unreadTotal = (totalApplicants?.newApplicant?.unread || 0) - afterAYearApplicants.unread;

  const dispatchFetchNewApplicants = useCallback(
    (subsStatus: string, lastElementDocs?: unknown, limit = 10, position = undefined) =>
      dispatch(
        fetchApplicantsListRequest({
          where: [
            ['applicationStatus.currentStatus', '==', 'New Applicant'],
            ['applicationStatus.subStatus', '==', subsStatus],
            ['isDeleted', '==', false],
            ['positionApplied', 'in', position],
            ['validUntil', '>', moment().toDate()],
          ],
          orderBy: [['validUntil', 'desc'], ['createdAt', 'desc']],
          limit,
          startAfter: lastElementDocs,
        }),
      ),
    [dispatch]
  );

  const dispatchFetchAfterAYearApplicants = useCallback(
    (subsStatus: string, status: string) =>
      dispatch(
        fetchApplicantsAfterAYearCount({
          where: [
            ['applicationStatus.currentStatus', '==', 'New Applicant'],
            ['applicationStatus.subStatus', '==', subsStatus],
            ['isDeleted', '==', false],
            ['validUntil', '<', moment().toDate()],
          ],
        }, status),
      ),
    [dispatch]
  );

  const dispatchSetSideNav = useCallback(
    (name: RootStateOrAny) => dispatch(setSideNav(name)),
    [dispatch]
  );

  useEffect(() => {
    dispatch(dispatchFetchNewApplicants('Unread'));
    dispatch(dispatchFetchAfterAYearApplicants('Unread', 'unread'));
    dispatch(dispatchFetchAfterAYearApplicants('Read', 'read'));
    dispatch(fetchTotalApplicantsRequest());
    dispatch(clearSpecificRequest(SEARCH_APPLICANT_REQUEST));
    return () => {
      dispatch(resetIsUpdateSuccessful());
      dispatch(clearSpecificRequest(FETCH_APPLICANTS_LIST_REQUEST));
      dispatch(resetApplicantsState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (applicantsList && applicantsList?.response) {
      const lists = applicantsList.response.data;
      const arrToObj = Map(arrayToObject(lists));
      const whereQuery = applicantsList.response.query.where;
      if (activeTab === '0' && whereQuery[1][2] === 'Unread') {
        const mapUnreadApplicantsData = Map(unreadApplicantsData);
        const newUnreadApplicantsList = mapUnreadApplicantsData.mergeDeep(arrToObj);
        setUnreadApplicantsData(newUnreadApplicantsList.toJS());
        setUnreadLastElement(applicantsList.response.lastElement);
      }
      if (activeTab === '1' && whereQuery[1][2] === 'Read') {
        const mapReadApplicantsData = Map(readApplicantsData);
        const newReadApplicantsList = mapReadApplicantsData.mergeDeep(arrToObj);
        setReadApplicantsData(newReadApplicantsList.toJS());
        setReadLastElement(applicantsList.response.lastElement);
      }
    }
  }, [applicantsList, activeTab]);

  const handleOnChange = (subStatus: string, pagination: any, filters: any) => {
    const { current: page, pageSize } = pagination;
    const filteredPosition = filters.position || undefined;
    dispatch(fetchTotalApplicantsRequest());
    if (subStatus === 'unread') {
      if (filters.position) {
        setCurrentUnreadPage(1);
      }

      setUnreadPageSize(pageSize);
      if (unReadPageSize === pageSize) {
        if (page > 1) {
          if (currentUnreadPage + 1 >= page) {
            dispatch(dispatchFetchNewApplicants('Unread', unreadLastElement, pageSize, filteredPosition));
          } else {
            dispatch(dispatchFetchNewApplicants('Unread', undefined, page * pageSize, filteredPosition));
          }
        } else {
          dispatch(dispatchFetchNewApplicants('Unread', undefined, pageSize, filteredPosition));
        }
        setCurrentUnreadPage(page);
      } else {
        setCurrentUnreadPage(1);
        dispatch(dispatchFetchNewApplicants('Unread', undefined, page * pageSize, filteredPosition));
      }
    } else {
      if (filters.position) {
        setCurrentReadPage(1);
      }

      setReadPageSize(pageSize);
      if (readPageSize === pageSize) {
        if (page > 1) {
          if (currentReadPage + 1 >= page) {
            dispatch(dispatchFetchNewApplicants('Read', readLastElement, pageSize, filteredPosition));
          } else {
            dispatch(dispatchFetchNewApplicants('Read', undefined, page * pageSize, filteredPosition));
          }
        } else {
          dispatch(dispatchFetchNewApplicants('Read', undefined, pageSize, filteredPosition));
        }
        setCurrentReadPage(page);
      } else {
        setCurrentReadPage(1);
        dispatch(dispatchFetchNewApplicants('Read', undefined, page * pageSize, filteredPosition));
      }
    }
  };

  const onTabClick = (key: string) => {
    setActiveTab(key);
    dispatch(fetchTotalApplicantsRequest());
    if (key === '0') {
      dispatch(dispatchFetchNewApplicants('Unread'));
      dispatch(dispatchFetchAfterAYearApplicants('Unread', 'unread'));
    } else {
      dispatch(dispatchFetchNewApplicants('Read'));
      dispatch(dispatchFetchAfterAYearApplicants('Read', 'read'));
    }
  };


  const onMapApplicantsData = (dataArr: any[]) =>
    dataArr.map((applicant: any, index: number) => {
      const applicantName = applicant.applicantName
        ? `${applicant.applicantName.firstName} ${applicant.applicantName.middleName} ${applicant.applicantName.lastName}`
        : applicant.fullName;
      return {
        key: index + 1,
        id: applicant.id,
        name: applicantName,
        position: applicant.positionApplied,
        contactNumber: applicant.contactNumber,
        email: applicant.email,
        dateApplied: `${moment(applicant.createdAt.toDate()).format(
          "MM/DD/YYYY hh:mm a"
        )}`,
        isApplicationRead:
          applicant.applicationStatus.subStatus === "Read",
        referredBy: applicant.referredBy,
      };
    }).sort(
      (a: any, b: any) => {
        const date1: any = new Date(a.dateApplied);
        const date2: any = new Date(b.dateApplied);
        return (date2 - date1);
      },
    );

  const filterReadApplicants = onMapApplicantsData(Object.values(readApplicantsData));
  const filterUnreadApplicants = onMapApplicantsData(Object.values(unreadApplicantsData));

  return (
    <NewApplicantsPage
      sideNav={sideNav}
      isLoading={applicantsList?.isLoading}
      dispatchSetSideNav={dispatchSetSideNav}
      onTabClick={onTabClick}
      readApplicants={filterReadApplicants}
      unreadApplicants={filterUnreadApplicants}
      readTotal={readTotal <= 0 ? 0 : readTotal}
      unreadTotal={unreadTotal <= 0 ? 0 : unreadTotal}
      onChange={handleOnChange}
      currentReadPage={currentReadPage}
      currentUnreadPage={currentUnreadPage}
    />
  );
};

export default NewApplicantsPageContainer;
